// Reveal-on-scroll hook using IntersectionObserver.
// Adds `revealed` boolean once the element scrolls into view.
import { useEffect, useRef, useState } from "react";

export const useReveal = ({ threshold = 0.2, rootMargin = "0px 0px -80px 0px", once = true } = {}) => {  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          if (once) io.disconnect();
        } else if (!once) {
          setRevealed(false);
        }
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, revealed];
};

// Parallax hook — translates element on Y as it scrolls through viewport.
// speed = 0 (no move) to 1 (moves at scroll speed).
export const useParallax = (speed = 0.2) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight;
      // element progress -1 (below screen) → 1 (above screen)
      const progress = (rect.top + rect.height / 2 - viewport / 2) / (viewport + rect.height);
      setOffset(-progress * 100 * speed);
      raf = null;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed]);

  return [ref, offset];
};

// Active section tracker — returns the id currently most visible in the viewport.
// Pass the list of section ids to watch. Uses IntersectionObserver.
export const useActiveSection = (ids = [], { rootMargin = "-40% 0px -50% 0px" } = {}) => {
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === "undefined") return;
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!elements.length) return;

    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        });
        if (visible.size === 0) {
          setActive(null);
          return;
        }
        // Pick the id whose section is most in view
        const best = [...visible.entries()].sort((a, b) => b[1] - a[1])[0][0];
        setActive(best);
      },
      { rootMargin, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids.join("|"), rootMargin]);
  return active;
};

